import clsx from 'clsx';
import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useContext,
  useMemo,
  type KeyboardEvent as ReactKeyboardEvent,
  type FocusEvent,
} from 'react';
import { useIntl } from 'react-intl';

import { _useAriaLabelingProps } from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';
import { _textStyleCSS as textStyleCSS } from '@dynatrace/strato-components/styles';

import { SelectedOverCalendarContext } from './contexts/SelectedOverCalendarContext.js';
import { DateRangeSelector } from './DateRangeSelector.js';
import { DateTimeInput } from './DateTimeInput.js';
import { dateTimeRangeCSS } from './DateTimeRangePicker.sty.js';
import { removePrecisionFromTimeValue } from './helpers.js';
import { useDateTimeRangePicker } from './hooks/useDateTimeRangePicker.js';
import type {
  DateTimeRangePickerProps,
  DateTimeRangePickerRef,
} from './types.js';
import { DateRangeSelectorRef, DateTimeInputRef } from './types.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';
import { inputGroupClassName } from '../../layouts/input-group/InputGroup.sty.js';
import { InputGroup } from '../../layouts/input-group/InputGroup.js';
import { fieldColorsCSS } from '../../styles/field.sty.js';
import { dateTimePickerCSS } from '../date-time-picker/DateTimePicker.sty.js';
import { focusDateTimeInput } from '../date-time-picker/utils/focusDateTimeInput.js';
import { FormFieldMessageContext } from '../forms-core/contexts/FormFieldMessageContext.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { useSyncedFormControlProps } from '../forms-core/hooks/useSyncedFormControlProps.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import {
  GRAIL_MAX_DATE,
  GRAIL_MIN_DATE,
} from '../forms-core/time/date-time.js';

const DateTimeRangePickerBody = forwardRef<
  DateTimeRangePickerRef,
  DateTimeRangePickerProps
>((props, forwardedRef) => {
  const {
    id: propId,
    precision = 'minutes',
    disabled: propDisabled,
    readOnly = false,
    name,
    style: consumerStyle,
    className: consumerClassName,
    min = GRAIL_MIN_DATE,
    max = GRAIL_MAX_DATE,
    required: propRequired,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'aria-disabled': ariaDisabledProp,
    defaultValue,
    value: propValue,
    onChange: propOnChange,
    placeholder: placeholderProp,
  }: DateTimeRangePickerProps = props;

  const ariaProps = _useAriaLabelingProps(props);

  const rootRef = useRef<HTMLDivElement>(null);
  const touchedContainerRef = useRef<HTMLDivElement>(null);
  const dateTimeFromRef = useRef<DateTimeInputRef>(null);
  const dateTimeToRef = useRef<DateTimeInputRef>(null);
  const dateRangeSelectorRef = useRef<DateRangeSelectorRef>(null);

  const {
    disabled,
    required,
    id: controlId,
    ariaDisabled,
  } = useSyncedFormControlProps({
    propDisabled,
    propRequired,
    propId,
    propAriaDisabled: ariaDisabledProp,
  });
  const disabledOrAriaDisabled = disabled || ariaDisabled || false;

  const {
    value,
    onChange,
    placeholder,
    onCopyCapture,
    onPasteCapture,
    onBlurTouched,
    onError,
    errorTo,
    errorFrom,
    validate,
  } = useDateTimeRangePicker({
    value: propValue,
    defaultValue,
    placeholder: placeholderProp,
    readOnly,
    min,
    max,
    precision,
    onChange: propOnChange,
    touchedContainerRef,
    validationInputRef: dateTimeFromRef,
  });
  const intl = useIntl();

  const {
    state: { showError },
    dispatch: formMessageDispatch,
  } = useFormFieldMessagesContext();

  // Provide root and input element reference and functions to interact with the calendar overlay
  useImperativeHandle(
    forwardedRef,
    (): DateTimeRangePickerRef => ({
      element: rootRef.current,
      inputRef: {
        from: dateTimeFromRef.current?.inputRef,
        to: dateTimeToRef.current?.inputRef,
      },
      open: () => dateRangeSelectorRef.current?.open(),
      close: () => dateRangeSelectorRef.current?.close(),
      focus: () => dateTimeFromRef.current?.focus?.(),
      validate() {
        validate(value.from, value.to, true);
        formMessageDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
      },
    }),
    [formMessageDispatch, validate, value.from, value.to],
  );

  const variantStyle = dateTimeRangeCSS({
    showError,
    disabled: disabledOrAriaDisabled,
    readOnly: readOnly,
  });

  const { focusClassName } = useFocusRing({
    focusWithin: true,
    isMinimal: false,
    disabled,
    color: showError ? 'critical' : 'neutral',
  });

  const dateTimeFromLabel = intl.formatMessage({
    defaultMessage: 'DateTime from',
    id: 'fMy6q/KKOqbvCA3H',
    description:
      'Textbox to enter a relative or absolute timeframe from value.',
  });

  const dateTimeToLabel = intl.formatMessage({
    defaultMessage: 'DateTime to',
    id: 'uuhhc3nEZptwtV2E',
    description: 'Textbox to enter a relative or absolute timeframe to value.',
  });
  const formFieldMessageContextValue = useMemo(
    () => ({
      state: {
        customMessages: [],
        formControlMessages: [],
        showError,
      },
    }),
    [showError],
  );

  return (
    <Flex
      ref={rootRef}
      flexDirection="column"
      data-testid={dataTestId}
      style={consumerStyle}
      className={consumerClassName}
      {...ariaProps}
    >
      <FormFieldMessageContext.Provider value={formFieldMessageContextValue}>
        <InputGroup style={{ gridTemplateColumns: '1fr auto' }}>
          <Flex
            ref={touchedContainerRef}
            className={clsx(
              inputGroupClassName,
              dateTimePickerCSS({
                readOnly,
                showError,
                disabled,
                type: 'datetime',
              }),
              textStyleCSS({ textStyle: 'base' }),
              fieldColorsCSS({
                color: showError ? 'critical' : 'neutral',
                disabled: disabledOrAriaDisabled,
                variant: 'emphasized',
              }),
              focusClassName,
              variantStyle,
            )}
            onCopyCapture={onCopyCapture}
            onPasteCapture={onPasteCapture}
            onBlur={onBlurTouched}
            tabIndex={disabled ? undefined : 0}
            onKeyDown={(e: ReactKeyboardEvent) => {
              // Forward the focus when a key is pressed in this element.
              if (e.target === touchedContainerRef.current) {
                focusDateTimeInput(null, dateTimeFromRef, dateTimeToRef);
              }
            }}
            onFocus={(e: FocusEvent) => {
              // Forward the focus only if it is coming from outside this component.
              // Focus the first spin button with textContent; otherwise, focus the input.
              if (!rootRef.current?.contains(e.relatedTarget)) {
                focusDateTimeInput(e, dateTimeFromRef, dateTimeToRef);
              }
            }}
          >
            <DateTimeInput
              ref={dateTimeFromRef}
              precision={precision}
              disabled={disabled}
              readOnly={readOnly}
              placeholder={placeholder.from}
              name={name !== undefined ? `${name}-from` : undefined}
              value={value.from}
              onChange={(value) => {
                // FIXME: All selectors using the spin buttons should support strings in the
                // onChange to inform the user that the current value is invalid, but not empty
                if (typeof value !== 'string') {
                  // controlled changes should remove the precision of the value
                  const precisionRemovedValue = removePrecisionFromTimeValue(
                    value,
                    precision,
                  );
                  onChange.from(precisionRemovedValue);
                }
              }}
              aria-label={dateTimeFromLabel}
              aria-disabled={ariaDisabled}
              min={min}
              max={max}
              required={required}
              onError={onError.from}
              controlId={propId ?? controlId}
            />

            <span>→</span>
            <DateTimeInput
              ref={dateTimeToRef}
              precision={precision}
              disabled={disabled}
              readOnly={readOnly}
              placeholder={placeholder.to}
              name={name !== undefined ? `${name}-to` : undefined}
              value={value.to}
              onChange={(value) => {
                // FIXME: All selectors using the spin buttons should support strings in the
                // onChange to inform the user that the current value is invalid, but not empty
                if (typeof value !== 'string') {
                  // controlled changes should remove the precision of the value
                  const precisionRemovedValue = removePrecisionFromTimeValue(
                    value,
                    precision,
                  );
                  onChange.to(precisionRemovedValue);
                }
              }}
              aria-label={dateTimeToLabel}
              aria-disabled={ariaDisabled}
              min={min}
              max={max}
              required={required}
              onError={onError.to}
            />
          </Flex>
          <DateRangeSelector
            ref={dateRangeSelectorRef}
            value={value}
            onChange={(selectedRange) => {
              const newFrom = selectedRange?.from ?? null;

              onChange.from(newFrom);
              onChange.to(selectedRange?.to ?? null);
              requestAnimationFrame(() => {
                focusDateTimeInput(null, dateTimeFromRef);
              });
            }}
            precision={precision}
            disabled={disabled}
            aria-disabled={ariaDisabled}
            readOnly={readOnly}
            errorFrom={errorFrom}
            errorTo={errorTo}
            error={showError}
            min={min}
            max={max}
            data-dtrum-mask={dataDtrumMask}
            data-dtrum-allow={dataDtrumAllow}
          />
        </InputGroup>
      </FormFieldMessageContext.Provider>
    </Flex>
  );
});

/**
 * Component to comfortably enter a timeframe in localized format for expressions.
 * @internal
 */
export const DateTimeRangePicker = forwardRef<
  DateTimeRangePickerRef,
  DateTimeRangePickerProps
>((props, forwardedRef) => {
  const { selectedOverCalendar, setSelectedOverCalendar } = useContext(
    SelectedOverCalendarContext,
  );

  const [fallbackSelectedOverCalendar, setFallbackSelectedOverCalendar] =
    useState<boolean | undefined>(false);

  const [selectedOverCalendarFlag, setSelectedOverCalendarFlag] = useState<
    boolean | undefined
  >(false);

  if (
    selectedOverCalendar !== undefined ||
    setSelectedOverCalendar !== undefined
  ) {
    return <DateTimeRangePickerBody {...props} ref={forwardedRef} />;
  }

  return (
    <SelectedOverCalendarContext.Provider
      value={{
        selectedOverCalendar: fallbackSelectedOverCalendar,
        setSelectedOverCalendar: setFallbackSelectedOverCalendar,
        calendarOnChangeFlag: selectedOverCalendarFlag,
        setCalendarOnChangeFlag: setSelectedOverCalendarFlag,
      }}
    >
      <DateTimeRangePickerBody {...props} ref={forwardedRef} />
    </SelectedOverCalendarContext.Provider>
  );
});
